import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ResendVerificationPanel from "@/components/auth/ResendVerificationPanel";
import { apiErrorCode, formatApiError, useAuth } from "@/context/AuthContext";
import { consumeLoginResume, navigateResumePath } from "@/lib/loginResume";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setShowResendVerification(false);
    setBusy(true);
    try {
      const verifiedUser = await login(form);
      const resume = consumeLoginResume();
      if (resume && navigateResumePath(navigate, resume)) {
        return;
      }
      const role = verifiedUser?.role;
      navigate(role === "admin" ? "/admin" : role === "instructor" ? "/instructor" : "/dashboard");
    } catch (err) {
      setError(formatApiError(err));
      if (apiErrorCode(err) === "email_not_verified") {
        setShowResendVerification(true);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card max-w-xl mx-auto space-y-6" data-testid="login-page">
      <h1 className="text-base md:text-lg font-semibold">Log in</h1>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1">
          <label htmlFor="login-email" className="font-medium">Email</label>
          <input
            id="login-email"
            data-testid="login-email-input"
            className="input"
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="login-password" className="font-medium">Password</label>
          <input
            id="login-password"
            data-testid="login-password-input"
            className="input"
            type="password"
            required
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
        </div>
        {error && <p role="alert" className="text-destructive" data-testid="login-error">{error}</p>}
        <button type="submit" className="btn-primary" disabled={busy} data-testid="login-submit-button">
          {busy ? "Logging in…" : "Log in"}
        </button>
      </form>
      {showResendVerification && (
        <div className="space-y-3" data-testid="login-email-verification-required">
          <p className="text-sm text-muted-foreground">
            Your password is set, but this account still needs email verification before you can log in. If you just
            used <strong>Forgot password</strong>, that step does not verify your email — use the button below to send a
            verification link.
          </p>
          <ResendVerificationPanel
            email={form.email}
            onDismiss={() => setShowResendVerification(false)}
          />
        </div>
      )}
      <div className="text-sm text-muted-foreground space-x-4">
        <Link to="/forgot-password" className="underline" data-testid="forgot-password-link">Forgot password?</Link>
        <Link to="/register" className="underline" data-testid="login-register-link">Register</Link>
      </div>
    </div>
  );
}
